import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { BaseStatusMessageProps } from '../../types/BaseStatusMessageProps';
import { BaseStatusTextProps } from '../../types/BaseStatusTextProps';
import { Input } from '../../types/Input';
import { Nullable } from '../../types/Nullable';
import { SharedButtonProps } from '../../types/SharedButtonProps';
import { Theme } from '../../types/Theme';
import { UserAction } from '../../types/UserAction';
import { typedMemo } from '../../utils/typedMemo';
import { Actions } from '../Actions/Actions';
import { Button } from '../Button/Button';
import { CopyButton } from '../Button/subcomponents/CopyButton';
import { IconButton } from '../IconButton/IconButton';
import { Label } from '../Label/Label';
import { Layout } from '../Layout/Layout';
import { Link } from '../Link/Link';
import { Markdown } from '../Markdown/Markdown';
import { MarkedText } from '../MarkedText/MarkedText';
import { StatusMessage } from '../StatusMessage/StatusMessage';
import { StatusText } from '../StatusText/StatusText';
import { Text } from '../Text/Text';
import { Tooltip } from '../Tooltip/Tooltip';

/** Inline gap between label text and (Required)/(Optional) — not translatable. */
const INLINE_LABEL_SEPARATOR = ' ';

type Props<V, M extends boolean> = {
  /** A valid Input component (e.g. `TextInput`, `NumberInput`, `Select`, `Checkbox`) */
  input: Nullable<React.ElementType>;
  /** Input label */
  label: string;
  /** FormField actions */
  actions?: UserAction[];
  actionsSize?: SharedButtonProps['size'];
  /** Custom child content (when `input` is explicitly set to `null`) */
  children?: React.ReactNode;
  /** Optional description */
  description?: string;
  /** Optional description link */
  descriptionLink?: {
    linkText: string;
    pathname: string;
    additionalText?: string;
    tooltip?: string;
    search?: string;
    callback?: () => void;
  };
  /** Toggle to activate the copy button for the input field.*/
  enableCopy?: boolean;
  /** Enable grey background */
  enableBackdrop?: boolean;
  /** Preview an example */
  example?: {
    content: React.ReactNode;
    label?: string;
  };
  /** Footer text */
  footer?: string;
  /** Information that will be displayed in info icon tooltip */
  info?: string;
  /** Input props */
  inputProps?: Object;
  /** Optional setting to toggle form field content collapsed/expanded */
  isCollapsible?: boolean;
  /** Explicitly indicate an input is optional */
  optional?: boolean;
  /** Explicitly indicate an input is required */
  required?: boolean;
  /** Optional status message */
  statusMessage?: BaseStatusMessageProps;
  /** Optional status text */
  statusText?: BaseStatusTextProps;
  /** Light / Dark Theme */
  theme?: Theme;
  /** Tooltip for the input (e.g. if it is disabled) */
  tooltip?: string;
  /** Highlights label, description or footer text if matching  */
  search?: string;
  /** Reserves an interactive Explain action button */
  onExplain?: () => void;
} & Input<V, M>;

export const FormField = typedMemo(
  <V extends unknown, M extends boolean>({
    actions = [],
    children,
    description,
    descriptionLink,
    enableCopy,
    enableBackdrop = false,
    example,
    footer,
    info,
    input: Input,
    inputProps: overrideInputProps = {},
    isCollapsible,
    label,
    optional,
    readOnly,
    required,
    // input props
    name,
    value,
    onChange,
    onExplain,
    disabled,
    id,
    error,
    success,
    placeholder,
    statusMessage,
    statusText,
    theme,
    tooltip,
    search = '',
    actionsSize,
  }: Props<V, M>) => {
    const intl = useIntl();
    const [isCollapsed, toggleCollapsible] = useState(false);
    const inputId = id ?? name;
    const labelId = `${inputId}-label`;
    const descriptionId = `${inputId}-description`;
    const footerId = `${inputId}-footer`;

    const footerText = error || success || footer;
    let footerTextColor: 'status.danger' | 'status.success' | 'text.quiet' =
      'text.quiet';
    if (error) {
      footerTextColor = 'status.danger';
    } else if (success) {
      footerTextColor = 'status.success';
    }

    const describedByString = `${description ? descriptionId : ''} ${
      footerText ? footerId : ''
    }`.trim();
    const ariaDescribedBy =
      description || footerText ? describedByString : undefined;

    const inputProps = {
      name,
      value,
      onChange,
      disabled,
      id: inputId,
      error,
      success,
      placeholder,
      readOnly,
      ...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {}),
      ...overrideInputProps,
    };

    const input = Input && <Input {...inputProps} />;
    const inputContent = enableCopy ? (
      <Layout spacing={1}>
        {input}
        <CopyButton copyText={String(value)} />
      </Layout>
    ) : (
      input
    );

    const linkDescription = descriptionLink && (
      <Text preset="description">
        {descriptionLink.additionalText}
        {INLINE_LABEL_SEPARATOR}
        <Link
          display="inline-flex"
          text={descriptionLink.linkText}
          tooltip={descriptionLink.tooltip}
          pathname={descriptionLink.pathname}
          search={descriptionLink.search}
          onClick={descriptionLink.callback}
        />
      </Text>
    );

    const descriptionText = description && (
      <Text preset="description" id={descriptionId} whiteSpace="pre-wrap">
        <MarkedText search={search} text={description} whiteSpace="pre-wrap" />
      </Text>
    );

    return (
      <Layout
        direction="column"
        minW="input.min-width"
        spacing={1}
        w="100%"
        aria-labelledby={labelId}
        bg={enableBackdrop ? 'background.quiet' : null}
        p={enableBackdrop ? 4 : null}
        borderRadius={enableBackdrop ? '6px' : null}
      >
        <Layout direction="column">
          <Layout align="center" spacing={2}>
            <Label htmlFor={inputId} id={labelId} info={info} theme={theme}>
              <MarkedText search={search} text={label} />
              {(required || optional) && INLINE_LABEL_SEPARATOR}
              {required && (
                <Text preset="help">
                  <FormattedMessage
                    id="FormField.required"
                    defaultMessage="(Required)"
                    description="Label indicating that the field is required"
                  />
                </Text>
              )}
              {optional && (
                <Text preset="help">
                  <FormattedMessage
                    id="FormField.optional"
                    defaultMessage="(Optional)"
                    description="Label indicating that the field is optional"
                  />
                </Text>
              )}
            </Label>
            {actions && <Actions actions={actions} size={actionsSize} />}
            {statusText && <StatusText {...statusText} />}
            {onExplain && (
              <Button
                text={intl.formatMessage({
                  id: 'FormField.explainThis',
                  defaultMessage: 'Explain this',
                  description: 'Label for the Explain button',
                })}
                variant="action"
                onClick={onExplain}
              />
            )}
            {isCollapsible && (
              <IconButton
                preset={isCollapsed ? 'show' : 'hide'}
                size="s"
                onClick={() => toggleCollapsible(!isCollapsed)}
              />
            )}
          </Layout>
          {!isCollapsed && (
            <>
              {descriptionText}
              {linkDescription}
              {example && (
                <Tooltip
                  isInverse
                  isInteractive
                  tooltip={
                    typeof example.content === 'string' ? (
                      <Markdown text={example.content} />
                    ) : (
                      example.content
                    )
                  }
                >
                  <Text color="action.link">
                    {example.label ??
                      intl.formatMessage({
                        id: 'FormField.showExample',
                        defaultMessage: 'Show example',
                        description: 'Label for example content',
                      })}
                  </Text>
                </Tooltip>
              )}
            </>
          )}
        </Layout>
        {!isCollapsed && (
          <>
            <Tooltip tooltip={tooltip} width="100%">
              <>{children ?? inputContent}</>
            </Tooltip>
            {footerText && (
              <Text
                id={footerId}
                color={footerTextColor}
                variant="tiny"
                role="note"
              >
                <MarkedText search={search} text={footerText} />
              </Text>
            )}
            {statusMessage && <StatusMessage {...statusMessage} />}
          </>
        )}
      </Layout>
    );
  },
);
